import { useEffect, useState } from 'react';
import { apiListProducts, type ProductDetail, type Shop } from '~/lib/api/shop';
import { classNames } from '~/utils/classNames';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CARD,
  LoadError,
  Pill,
  Section,
  SkeletonCard,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { NewProductForm } from './NewProductForm';
import { ProductDetailView } from './ProductDetailView';
import { priceRangeLabel, productStatusMeta, productStockLabel } from './shop-model';

export function ProductsView({ appId, shop }: { appId: string; shop: Shop }) {
  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const products = useResource(() => apiListProducts(appId, { q }), `${appId}:${q}`);

  // Debounce the search so typing doesn't fire a request per key.
  useEffect(() => {
    const t = setTimeout(() => setQ(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  if (selected) {
    return (
      <ProductDetailView
        appId={appId}
        shop={shop}
        productId={selected}
        onBack={() => {
          setSelected(null);
          void products.reload();
        }}
      />
    );
  }

  const list = products.data ?? [];
  const brandNew = !q && products.data !== null && list.length === 0;

  const addButton = !creating && !brandNew && (
    <button onClick={() => setCreating(true)} className={BTN_GHOST}>
      <div className="i-ph:plus text-[13px]" />
      New product
    </button>
  );

  return (
    <Section title="Products" icon="i-ph:package" aside={addButton}>
      {creating && (
        <NewProductForm
          appId={appId}
          shop={shop}
          onDone={(created) => {
            setCreating(false);

            if (created) {
              setSelected(created.product.id);
            }
          }}
        />
      )}

      {!brandNew && (
        <div className="relative">
          <div className="i-ph:magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-muted" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by title, slug or tag"
            aria-label="Search products"
            className="ui-input !py-2 pl-8"
          />
        </div>
      )}

      {products.loading && !products.data ? (
        <SkeletonCard lines={3} />
      ) : products.error ? (
        <LoadError message="Could not load products." onRetry={() => void products.reload()} />
      ) : brandNew ? (
        !creating && <EmptyShop onCreate={() => setCreating(true)} />
      ) : list.length === 0 ? (
        <div className={classNames(CARD, 'px-4 py-5 text-center')}>
          <p className="text-[12px] text-muted">No products match “{q}”.</p>
        </div>
      ) : (
        <div className={classNames(CARD, 'divide-y divide-hairline overflow-hidden')}>
          {list.map((detail) => (
            <ProductRow
              key={detail.product.id}
              detail={detail}
              currency={shop.currency}
              onOpen={() => setSelected(detail.product.id)}
            />
          ))}
        </div>
      )}
    </Section>
  );
}

function ProductRow({ detail, currency, onOpen }: { detail: ProductDetail; currency: string; onOpen: () => void }) {
  const status = productStatusMeta(detail.product.status);
  const live = detail.variants.filter((v) => !v.archivedAt);
  const stock = productStockLabel(detail.variants);
  const image = detail.images[0];

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-white/[0.02]"
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-hairline bg-sunken">
        {image ? (
          <img src={image.url} alt={image.alt ?? ''} className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="i-ph:package text-[14px] text-faint" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-[13px] font-medium text-primary">{detail.product.title}</span>
          <Pill tone={status.tone} label={status.label} />
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[11px] text-muted">
          <span>
            {live.length} variant{live.length === 1 ? '' : 's'}
          </span>
          <span aria-hidden>·</span>
          <span className={stock.tone === 'danger' ? 'text-danger' : undefined}>{stock.label}</span>
        </div>
      </div>
      <div className="shrink-0 font-geist-mono text-[12px] text-primary">
        {priceRangeLabel(detail.variants, currency)}
      </div>
      <div className="i-ph:caret-right shrink-0 text-[12px] text-faint" />
    </button>
  );
}

function EmptyShop({ onCreate }: { onCreate: () => void }) {
  return (
    <div className={classNames(CARD, 'px-5 py-7 text-center')}>
      <div className="i-ph:storefront mx-auto mb-3 text-2xl text-faint" />
      <p className="ui-serif text-[20px] leading-tight text-primary">An empty shelf, for now</p>
      <p className="mx-auto mt-2 max-w-[290px] text-[12px] leading-relaxed text-muted">
        Ask the agent in chat, like <span className="text-secondary">“add a blue mug at ₹499”</span>, or add a product
        here. Products show in your storefront only once they are <span className="text-secondary">Active</span>.
      </p>
      <button onClick={onCreate} className={classNames(BTN_SECONDARY, 'mt-4')}>
        <div className="i-ph:plus text-[13px]" />
        New product
      </button>
    </div>
  );
}
